// Detects whether the currently-loaded template's shape has drifted from what was saved into
// widget_state. A restore replays the saved headerParams/params/buttonUrlParam/ctaButtonUrl
// against whatever template currently resolves for the stored templateId — if the template's
// variable counts or dynamic-button field changed since the save, those values land in the wrong
// slots. This only detects the mismatch; Message/index.js decides what to do with it (warn, but
// still restore — Bloomreach already holds the old webhook either way).

// The wire-JSON checks live in src/templateSend/shared/ so the template switch asks the very same
// question before it carries a saved message over (see its README.md for the swap-out plan). After the
// swap they move to src/webhook/ or src/helper/; update this path then, nothing else here changes.
import { cardsMatch, ltoExpiryMatches, DYNAMIC_BUTTON_FIELDS } from '../templateSend';
import { messageTypeForTemplate } from './messageType';

// Mirrors gs-integration-component's variableCount (Helper/Utils.tsx): each {{n}} occurrence is
// one variable slot. Body may be numbered continuing from the header (e.g. {{2}}, {{3}} when the
// header already used {{1}}) — irrelevant here, since this counts occurrences, not the digits.
const TEMPLATE_VAR_RE = /\{\{\s*\d+\s*\}\}/g;

function countTemplateVariables(text) {
  return (String(text || '').match(TEMPLATE_VAR_RE) || []).length;
}

// button_type -> the draft field its dynamic suffix lives in (see buildDefinition.js / the
// TemplateMessaging submit handler in Message/index.js). Anything else (STATIC, NONE, COMPLEX,
// ...) uses neither field.
const expectedButtonField = (buttonType) => DYNAMIC_BUTTON_FIELDS[buttonType]?.field ?? null;

const restoredButtonField = (restored) => Object.values(DYNAMIC_BUTTON_FIELDS)
  .map(({ field }) => field)
  .find((field) => restored[field]) ?? null;

// Catches a header-kind change that keeps the variable count at 0 either side (e.g. LOCATION ->
// IMAGE, or IMAGE -> GIF) — invisible to the header-count check above. Uses messageTypeForTemplate
// so this can't disagree with what a restore actually sends (see Message/index.js's own computeState).
// Skipped for CAROUSEL (cardsMatch already covers it) and for a draft saved without a
// messageType — widget_state doesn't persist it yet (toWidgetState/fromWidgetState), so there is
// nothing to compare; this starts catching drift once that lands.
function messageTypeMatches(template, restored) {
  if (!restored.messageType || template?.details?.button_type === 'CAROUSEL') return true;
  const currentMessageType = messageTypeForTemplate(template);
  return !currentMessageType || currentMessageType === restored.messageType;
}

// True when the restored draft's shape no longer matches the current template: header/body
// variable count changed, the template's dynamic-button type no longer matches which of
// buttonUrlParam/ctaButtonUrl was saved, (CAROUSEL only) a card's own shape changed (count, media
// format, body variables, dynamic buttons), the header kind changed without moving the variable
// count, or an LTO template's expiration setting changed.
export function hasTemplateDrifted(template, restored) {
  const details = template?.details;
  if (!details || !restored) return false;

  const headerCountMatches = countTemplateVariables(details.header) === (restored.headerParams || []).length;
  const bodyCountMatches = countTemplateVariables(details.body) === (restored.params || []).length;
  const buttonFieldMatches = expectedButtonField(details.button_type) === restoredButtonField(restored);
  // The checks above only see a carousel's top-level header/body/button_type; real drift lives inside each
  // card (media format, body variable count, which buttons are dynamic-URL).
  const carouselMatches = details.button_type !== 'CAROUSEL' || cardsMatch(details.carouselCards, restored.waTemplateJson);
  const ltoMatches = details.button_type !== 'LTO' || ltoExpiryMatches(details, restored.waTemplateJson);

  return !(headerCountMatches && bodyCountMatches && buttonFieldMatches && carouselMatches
    && messageTypeMatches(template, restored) && ltoMatches);
}
